import type { ReactNode } from 'react'
import { ChevronsDownUp, ChevronsUpDown } from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { testIds } from '@/lib/test-ids'

export type BrowseLevelFocusMode = 'expand' | 'collapse'

/** Shared coordinator for one parent's focus zone (CSS-driven hover — no per-row React state). */
export type BrowseLevelFocusCoordinator = {
  parentPath: string
  wsColor: string
  /** Collapse/expand button (parent chevron column). */
  gutterLeftPx: number
  mode: BrowseLevelFocusMode
  onAction: () => void
}

/** Full-height line at this parent's browse depth (prototype); one active zone via CSS :has. */
export function BrowseLevelFocusZone({
  coordinator,
  children,
}: {
  coordinator: BrowseLevelFocusCoordinator
  children: ReactNode
}) {
  return (
    <div
      className="browse-level-focus-zone relative min-w-0"
      style={
        {
          '--ws-color': coordinator.wsColor,
          '--browse-level-focus-gutter-left': `${coordinator.gutterLeftPx}px`,
        } as React.CSSProperties
      }
    >
      <div className="browse-level-focus-zone__line pointer-events-none" aria-hidden />
      {children}
    </div>
  )
}

/** Collapse/expand control on the hovered row (CSS shows slot on row hover). */
export function BrowseLevelFocusRowControl({
  coordinator,
}: {
  coordinator: BrowseLevelFocusCoordinator
}) {
  const isCollapse = coordinator.mode === 'collapse'
  const label = isCollapse
    ? 'Collapse to recent items (activity filter)'
    : 'Show all items at this level'

  return (
    <div className="browse-level-focus-row__slot">
      <Tooltip delayDuration={400}>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="browse-level-focus-row__button pointer-events-auto"
            data-testid={testIds.explorerNode.levelFocusButton(
              coordinator.parentPath,
              coordinator.mode
            )}
            aria-label={label}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation()
              coordinator.onAction()
            }}
          >
            {isCollapse ? (
              <ChevronsDownUp
                className="browse-level-focus-row__icon h-3 w-3"
                aria-hidden
              />
            ) : (
              <ChevronsUpDown
                className="browse-level-focus-row__icon h-3 w-3"
                aria-hidden
              />
            )}
          </button>
        </TooltipTrigger>
        <TooltipContent side="right" className="text-xs max-w-[220px]">
          {label}
        </TooltipContent>
      </Tooltip>
    </div>
  )
}
